import React, { useMemo } from 'react';
import Plot from 'react-plotly.js';

// Fixed color dictionaries
const consequenceColorMap = {
  "missense_variant": '#e6194b',
  "synonymous_variant": '#3cb44b',
  "stop_gained": '#ffe119',
  "frameshift_variant": '#4363d8',
  "inframe_insertion": '#f58231',
  "start_lost": '#911eb4',
  "splice_region_variant": '#46f0f0',
  "inframe_deletion": '#f032e6',
  "protein_altering_variant": '#bcf60c',
  "NMD_transcript_variant": '#fabebe',
  "coding_sequence_variant": '#008080',
  "incomplete_terminal_codon_variant": '#e6beff',
  "splice_acceptor_variant": '#9a6324',
  "stop_retained_variant": '#fffac8',
  "splice_donor_region_variant": '#800000',
  "start_retained_variant": '#aaffc3',
  "splice_donor_5th_base_variant": '#808000'
};

const getDomainsFromDatatable = (data) => {
    const domains = [];
    let currentDomain = null;
    let startPos = null;

    data.forEach((row, index) => {
        const domainName = row.unDomains;
        const pos = parseInt(row.anPosition.split("_")[1]);

        if (domainName && domainName !== "-") {
            if (currentDomain === null) {
                currentDomain = domainName;
                startPos = pos;
            } else if (currentDomain !== domainName) {
                domains.push({ name: currentDomain, start: startPos, end: pos - 1, type: 'domain' });
                currentDomain = domainName;
                startPos = pos;
            }
        } else {
            if (currentDomain !== null) {
                domains.push({ name: currentDomain, start: startPos, end: pos - 1, type: 'domain' });
                currentDomain = null;
                startPos = null;
            }
        }

        if (index === data.length - 1 && currentDomain !== null) {
            domains.push({ name: currentDomain, start: startPos, end: pos, type: 'domain' });
        }
    });

    return domains;
};

const getPfamDomainsFromDatatable = (data) => {
    const domains = [];
    let currentDomain = null;
    let startPos = null;

    data.forEach((row, index) => {
        const domainName = row.pfamDomainPosHmm.split("_")[0];
        const pos = parseInt(row.anPosition.split("_")[1]);

        if (domainName && domainName !== "-") {
            if (currentDomain === null) {
                currentDomain = domainName;
                startPos = pos;
            } else if (currentDomain !== domainName) {
                domains.push({ name: currentDomain, start: startPos, end: pos - 1, type: 'domain' });
                currentDomain = domainName;
                startPos = pos;
            }
        } else {
            if (currentDomain !== null) {
                domains.push({ name: currentDomain, start: startPos, end: pos - 1, type: 'domain' });
                currentDomain = null;
                startPos = null;
            }
        }

        if (index === data.length - 1 && currentDomain !== null) {
            domains.push({ name: currentDomain, start: startPos, end: pos, type: 'domain' });
        }
    });

    return domains;
};


const getInterDomainsFromDatatable = (data) => {
    const domains = [];
    let currentDomain = null;
    let startPos = null;

    data.forEach((row, index) => {
        const domainName = row.interproDomain.split("_")[0];
        const pos = parseInt(row.anPosition.split("_")[1]);

        if (domainName && domainName !== "-") {
            if (currentDomain === null) {
                currentDomain = domainName;
                startPos = pos;
            } else if (currentDomain !== domainName) {
                domains.push({ name: currentDomain, start: startPos, end: pos - 1, type: 'domain' });
                currentDomain = domainName;
                startPos = pos;
            }
        } else {
            if (currentDomain !== null) {
                domains.push({ name: currentDomain, start: startPos, end: pos - 1, type: 'domain' });
                currentDomain = null;
                startPos = null;
            }
        }

        if (index === data.length - 1 && currentDomain !== null) {
            domains.push({ name: currentDomain, start: startPos, end: pos, type: 'domain' });
        }
    });

    return domains;
};

const ProteinNeedlePlot = ({ variants, annotations_type, structureData, onVariantClick, datatable, activeTabIndex}) => {

  const chartData = useMemo(() => {
    if (!variants) return { x: [], y: [], colors: [], text: [], raw: [] };

    const x = [], y = [], colors = [], text = [], sizes = [];

    variants.forEach(v => {
      const pos = parseInt(v.anPosition.split("-")[1] || v.col2 || v.proteinPosition);
      if (!pos) return;

      let rank = 2;
      const conseq = (v.consequence || v.col4 || '').toLowerCase();
      if(conseq.includes('synonymous_variant')) rank = 1;
      else if(conseq.includes('missense_variant')) rank = 3;

      const yValue = rank === 1 ? 2 : (rank === 2 ? 1.5 : 1);

      x.push(pos);
      y.push(yValue);
      colors.push(consequenceColorMap[conseq]);
      sizes.push(rank === 1 ? 12 : 8);
    });

    return { x, y, colors, text, sizes, raw: variants };
  }, [variants]);


  const posCounts = variants.reduce((acc, v) => {
    const pos = parseInt(v.anPosition.split("-")[1] || v.col2 || v.proteinPosition);
    if (!pos) return acc;
    acc[pos] = (acc[pos] || 0) + 1;
    return acc;
}, {});

const parseDomainPos = (posStr) => {
        if (!posStr || posStr === "-" || typeof posStr !== 'string') return { start: 0, end: 0 };
        const parts = posStr.split("-");
        return {
            start: parseInt(parts[0]) || 0,
            end: parseInt(parts[1]) || 0
        };
    };

const { backbone, needles, structures, domainHoverTraces } = useMemo(() => {

    const backbone = {
        type: 'rect',
        x0: 0,
        x1: datatable.length,
        y0: -0.3,
        y1: 0.3,
        fillcolor: '#d3d3d3',
        line: { width: 1, color: '#333' },
        layer: 'below'
    };

    const needles = Object.keys(posCounts).map(pos => {
        const p = parseInt(pos);
        return {
            type: 'line',
            x0: p, x1: p,
            y0: 0.3,
            y1: posCounts[p],
            line: { color: '#444', width: 1 },
            layer: 'below'
        };
    });

    let currentDomains = [];
    if (annotations_type === 'unDomains'  && datatable && activeTabIndex === 5) {
        currentDomains = getDomainsFromDatatable(datatable);
    } else if (annotations_type === 'interPro'  && datatable && activeTabIndex === 5) {
        currentDomains = getInterDomainsFromDatatable(datatable);
    } else if (annotations_type === 'pfamDomainPosHmm' && datatable && activeTabIndex === 5) {
        currentDomains = getPfamDomainsFromDatatable(datatable);
    }

    const structures = currentDomains.map(d => ({
        type: 'rect',
        x0: d.start,
        x1: d.end,
        y0: -0.3,
        y1: 0.3,
        fillcolor: '#F1C40F',
        line: { width: 1, color: '#333' },
        layer: 'below'
    }));

    const domainHoverTraces = {
        x: currentDomains.map(d => (d.start + d.end) / 2),
        y: currentDomains.map(() => 0),
        text: currentDomains.map(d => `Domain: ${d.name}<br>Start: ${d.start}<br>End: ${d.end}`),
        mode: 'markers',
        type: 'scatter',
        hoverinfo: 'text',
        showlegend: false,
        marker: {
            size: 25,
            color: 'rgba(0,0,0,0)'
        }
    };

    return { backbone, needles, structures, domainHoverTraces };
}, [variants, annotations_type, datatable.length, posCounts, activeTabIndex]);


const createTrace = (name, color) => {
        const filtered = variants.filter(v => (v.consequence || '').toLowerCase().includes(name.toLowerCase()));
        if (filtered.length === 0) return null;

        const posMap = filtered.reduce((acc, v) => {
            const pos = parseInt(v.anPosition?.split("-")[1] || v.col2 || v.proteinPosition);
            if (pos) {
                if (!acc[pos]) acc[pos] = [];
                acc[pos].push(v);
            }
            return acc;
        }, {});

        const xValues = Object.keys(posMap).map(Number);
        return {
            x: xValues,
            y: xValues.map(p => posCounts[p]),
            name: name,
            mode: 'markers', type: 'scatter',
            marker: { size: 12, color: color, line: { color: 'white', width: 1 } },
            text: xValues.map(p => {
                const allAtPos = variants.filter(v => parseInt(v.anPosition?.split("-")[1] || v.col2 || v.proteinPosition) === p);
                const muts = allAtPos.map(v => `• ${v.aaChange || 'N/A'} (${v.consequence.split(/[,/]/)[0]})`).join('<br>');
                return `<b>Position ${p}</b> (Total: ${allAtPos.length})<br>${muts}`;
            }),
            hoverinfo: 'text'
        };
    };

const uniqueConsequences = [...new Set(variants.map(v => v.consequence.split(/[,/]/)[0].trim()))];
    const traces = uniqueConsequences
        .map(c => createTrace(c, consequenceColorMap[c] || '#7f8c8d'))
        .filter(t => t !== null);

const finalData = [...traces, domainHoverTraces];


  return (
    <div className="card" style={{ width: '100%' }}>

      <div style={{ width: '100%', height: '400px' }}>
        <Plot
      data={finalData}
      layout={{
        showlegend: true,
        legend: { orientation: 'h', y: -0.3, x: 0.5, xanchor: 'center' },
        autosize: true,
        margin: { l: 50, r: 40, t: 20, b: 80 },
        hovermode: 'closest',
        xaxis: {
          range: [0, datatable.length],
          dtick: 50,
          showgrid: false,
          zeroline: false,
          title: "Amino Acid Position"
        },
        yaxis: {
          title: "Mutation Count",
          range: [-1, Math.max(...Object.values(posCounts), 3) + 1],
          showticklabels: true,
          showgrid: true,
          gridcolor: '#eee',
          zeroline: true,
          rangemode: 'nonnegative',
          dtick: 1
        },
        shapes: [backbone, ...needles, ...structures]
      }}
  useResizeHandler={true}
  style={{ width: "100%", height: "100%" }}
  config={{ responsive: true, displaylogo: false }}
  onClick={(data) => {
    if (onVariantClick && data.points[0]) {
      onVariantClick(data.points[0].customdata);
    }
  }}
/>
      </div>
    </div>
  );
};

export default ProteinNeedlePlot;